import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { useModalContext } from 'context/modalContext'
import { deleteMediaCategory } from 'services/endpoints/mediaCategory'
import './_delete-category-modal.scss'
import { useProfileCacheStore } from 'store/profileCacheStore'

interface Props {
  selectedCategory: { name: string; id: string | null }
  closeDrawer: () => void
}

export const DeleteCategoryModal = ({ selectedCategory, closeDrawer }: Props) => {
  const modalData = useModalContext()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const deleteCategory = useMutation(
    async () => await deleteMediaCategory(selectedCategory.id),
    {
onSuccess: () => {
  modalData.clearModal()
  closeDrawer()

  const deletedId = selectedCategory.id
  if (!deletedId) return

  // -----------------------------
  // 1) React Query: patch user caches that contain all_media_categories
  // -----------------------------
  queryClient.setQueriesData(
    {
      predicate: (q) => {
        const d: any = q.state.data
        const u = d?.data ?? d
        return Array.isArray(u?.all_media_categories)
      }
    },
    (old: any) => {
      if (!old) return old
      const u = old?.data ?? old
      const prev = Array.isArray(u?.all_media_categories) ? u.all_media_categories : []
      if (!prev.some((c: any) => c?.id === deletedId)) return old

      const nextUser = { ...u, all_media_categories: prev.filter((c: any) => c?.id !== deletedId) }
      return old?.data ? { ...old, data: nextUser } : nextUser
    }
  )

  // -----------------------------
  // 2) React Query: remove from ALL MediaCategories queries
  //    regardless of search, sorting, or ordering
  // -----------------------------
  queryClient.setQueriesData(
    'allMediaCategories',
    (old: any) => {
      if (!old || !Array.isArray(old.data)) return old

      const filtered = old.data.filter(
        (category: any) =>
          String(category.id) !== String(deletedId)
      )

      if (filtered.length === old.data.length) return old

      return {
        ...old,
        data: filtered,
      }
    }
  )
  // -----------------------------
  // 3) Zustand: remove from any cached creator profile that contains it
  // -----------------------------
  try {
    const store = useProfileCacheStore.getState()
    const cache = store.cache

    Object.entries(cache).forEach(([key, prof]) => {
      const prevCats = Array.isArray((prof as any)?.all_media_categories)
        ? (prof as any).all_media_categories
        : []

      if (!prevCats.some((c: any) => c?.id === deletedId)) return

      const [rawUsername, rawViewer] = String(key).split('::')
      const username = (prof as any)?.username ?? rawUsername
      const viewerId = rawViewer || null

      store.setProfile(
        username,
        {
          ...(prof as any),
          all_media_categories: prevCats.filter(
            (c: any) => c?.id !== deletedId
          ),
        },
        viewerId
      )
    })
  } catch (e) {
    console.warn('[DeleteCategoryModal] failed to patch profile-cache zustand', e)
  }
},


      onError: () => {
        addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
        modalData.clearModal()
        closeDrawer()
      },
    }
  )

  return (
    <div className='delete-category-modal-container'>
      <p className='delete-category-modal-text'>
        {t('areYouSureYouWantToDelete')}{' '}
        <span className='delete-category-name'>{selectedCategory.name} </span>?
      </p>

      <div className='delete-category-modal-button-container'>
        <Button
          text={t('back')}
          color='black'
          clickFn={() => {
            modalData.clearModal()
          }}
          height='3'
          width='13'
          font='mont-14-normal'
        />

        <Button
          text={t('delete')}
          color='blue'
          customClass={`delete-category-modal-button ${
            deleteCategory.isLoading ? 'delete-category-modal-button-loading' : ''
          }`}
          clickFn={() => {
            deleteCategory.mutate()
          }}
          height='3'
          width='13'
          font='mont-14-normal'
        />
      </div>
    </div>
  )
}
